import { useQuery } from '@tanstack/react-query';

import type { GetMarkReceivedAtFulfillmentCenterResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get received at fulfillment center status from a purchased vehicle
 */
export const getMarkReceivedAtFulfillmentCenter = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkReceivedAtFulfillmentCenterResponse> => {
	const response = await apiClient.get<GetMarkReceivedAtFulfillmentCenterResponse>(
		ordersEndpoints.markReceivedAtFulfillmentCenter(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting received at fulfillment center status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkReceivedAtFulfillmentCenter = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markReceivedAtFulfillmentCenter.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error(
					'Unable to get received at fulfillment center status without an order ID.',
				);
			}
			return getMarkReceivedAtFulfillmentCenter(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
